import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { Chargement, EnTetePage, Etiquette, Message, Vide } from "@cabinet/charte";
import { dossiersApi, ecrituresApi, ErreurApi, type PieceVue } from "../api/client";
import { SelecteurDExercice } from "../composants/Exercices";
import { useExerciceCourant } from "../composants/exerciceCourant";
import { GrilleDeSaisie } from "../composants/GrilleDeSaisie";
import { LignesDePiece } from "../composants/Piece";
import { LiensDExport } from "../composants/Exports";
import { JustificatifsDePiece } from "../composants/Justificatifs";
import { formaterDate, formaterMontant } from "../domaine/formats";
import { useSession } from "../session";

/**
 * Journal d'un exercice : les pièces, en brouillard ou validées, et leur
 * saisie dans la grille au clavier (EF-0402).
 *
 * L'exercice se choisit en tête d'écran ; la date d'une pièce, elle, décide
 * seule de l'exercice auquel la pièce se rattache. Les deux ne peuvent pas
 * diverger : une pièce datée hors de l'exercice affiché apparaît dans l'autre.
 */
export function PageEcritures() {
  const { id = "" } = useParams();
  const { peut } = useSession();
  const cache = useQueryClient();
  const [journal, setJournal] = useState("");

  const [formulaire, setFormulaire] = useState<{ ouvert: boolean; piece: PieceVue | null }>({
    ouvert: false,
    piece: null,
  });
  const [erreur, setErreur] = useState<string | null>(null);
  // Une pièce désignée dans l'adresse — depuis le grand livre, la balance
  // ou un lettrage — s'ouvre dépliée, dans son exercice.
  const [parametres] = useSearchParams();
  const cible = parametres.get("piece");
  // Le filtre de statut peut venir de l'adresse : l'import mène aux brouillards.
  const [statut, setStatut] = useState(parametres.get("statut") ?? "");
  const [deplies, setDeplies] = useState<Set<string>>(() => new Set(cible != null ? [cible] : []));

  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => dossiersApi.consulter(id) });
  const journaux = useQuery({ queryKey: ["journaux", id], queryFn: () => ecrituresApi.journaux(id) });
  const pieceCible = useQuery({
    queryKey: ["piece", cible],
    queryFn: () => ecrituresApi.consulter(cible!),
    enabled: cible != null,
  });
  const { exercices, exercice, choisir } = useExerciceCourant(id, pieceCible.data?.exercice);

  const pieces = useQuery({
    queryKey: ["pieces", exercice?.id, journal, statut],
    queryFn: () => ecrituresApi.pieces(exercice!.id, { journal: journal || undefined, statut: statut || undefined }),
    enabled: exercice != null,
  });

  const rafraichir = () => {
    void cache.invalidateQueries({ queryKey: ["pieces"] });
    void cache.invalidateQueries({ queryKey: ["grand-livre"] });
    void cache.invalidateQueries({ queryKey: ["balance"] });
    void cache.invalidateQueries({ queryKey: ["chaine", id] });
  };
  const signaler = (echec: unknown) =>
    setErreur(echec instanceof ErreurApi ? echec.message : "L'opération n'a pas abouti.");

  const validation = useMutation({
    mutationFn: (piece: string) => ecrituresApi.valider(piece),
    onSuccess: rafraichir,
    onError: signaler,
  });
  const suppression = useMutation({
    mutationFn: (piece: string) => ecrituresApi.supprimer(piece),
    onSuccess: rafraichir,
    onError: signaler,
  });
  const contrepassation = useMutation({
    mutationFn: ({ piece, date, motif }: { piece: string; date: string; motif: string | null }) =>
      ecrituresApi.contrepasser(piece, date, motif),
    onSuccess: rafraichir,
    onError: signaler,
  });

  function demanderLaContrepassation(piece: PieceVue) {
    setErreur(null);
    const date = window.prompt("Date de la contrepassation (AAAA-MM-JJ) :", piece.date);
    if (date == null || date.trim() === "") return;
    const motif = window.prompt("Motif (facultatif) :", "");
    contrepassation.mutate({ piece: piece.id, date: date.trim(), motif: motif?.trim() || null });
  }

  function demanderLaSuppression(piece: PieceVue) {
    setErreur(null);
    if (!window.confirm(`Supprimer le brouillard « ${piece.libelle} » ?`)) return;
    suppression.mutate(piece.id);
  }

  function basculer(piece: string) {
    setDeplies((courants) => {
      const suivants = new Set(courants);
      if (suivants.has(piece)) suivants.delete(piece);
      else suivants.add(piece);
      return suivants;
    });
  }

  // La pièce désignée se montre à l'écran dès que la liste la contient.
  const cibleAffichee = cible != null && (pieces.data?.some((p) => p.id === cible) ?? false);
  useEffect(() => {
    if (cibleAffichee) document.getElementById(`piece-${cible}`)?.scrollIntoView({ block: "center" });
  }, [cibleAffichee, cible]);

  const chargementInitial = dossier.isPending || journaux.isPending;
  const erreurDeChargement = dossier.error ?? journaux.error ?? pieces.error;

  return (
    <>
      <EnTetePage
        surTitre={dossier.data?.raisonSociale ?? "Dossier"}
        titre="Écritures"
        description="Une pièce naît en brouillard, modifiable et supprimable. Validée, elle reçoit son numéro, continu par journal et par exercice, et son empreinte, chaînée à la précédente : elle ne se corrige plus que par contrepassation."
        actions={
          peut("SAISIR_ECRITURE") && exercice?.accepteLaSaisie ? (
            <button
              type="button"
              className={formulaire.ouvert ? undefined : "principal"}
              onClick={() => setFormulaire((f) => ({ ouvert: !f.ouvert, piece: null }))}
            >
              {formulaire.ouvert ? "Annuler" : "Saisir une pièce"}
            </button>
          ) : undefined
        }
      />

      <div className="filtres-plan">
        <SelecteurDExercice exercices={exercices} exercice={exercice} surChoix={choisir} />
        <select value={journal} onChange={(e) => setJournal(e.target.value)} aria-label="Journal">
          <option value="">Tous les journaux</option>
          {(journaux.data ?? []).map((j) => (
            <option key={j.code} value={j.code}>
              {j.code} — {j.libelle}
            </option>
          ))}
        </select>
        <select value={statut} onChange={(e) => setStatut(e.target.value)} aria-label="Statut">
          <option value="">Brouillards et validées</option>
          <option value="BROUILLARD">Brouillards seulement</option>
          <option value="VALIDEE">Validées seulement</option>
        </select>
        {exercice != null && <LiensDExport exercice={exercice.id} formats={["fec", "ecritures"]} />}
      </div>

      {erreurDeChargement != null && (
        <Message
          type="erreur"
          titre={erreurDeChargement instanceof ErreurApi ? erreurDeChargement.titre : "Erreur"}
        >
          <p style={{ margin: "6px 0 0" }}>{erreurDeChargement.message}</p>
        </Message>
      )}

      {erreur != null && <Message type="erreur">{erreur}</Message>}

      {formulaire.ouvert && dossier.data != null && exercice != null && (
        <GrilleDeSaisie
          key={formulaire.piece?.id ?? "nouvelle"}
          dossier={id}
          codeDuReferentiel={dossier.data.codeDuReferentiel}
          devise={dossier.data.devise}
          exercice={exercice}
          journaux={(journaux.data ?? []).filter((j) => j.accepteLaSaisieManuelle)}
          piece={formulaire.piece}
          peutValider={peut("VALIDER_ECRITURE")}
          surChangement={rafraichir}
          surFermeture={() => setFormulaire({ ouvert: false, piece: null })}
        />
      )}

      {chargementInitial && <Chargement quoi="le dossier" />}

      {!chargementInitial && exercices != null && exercices.length === 0 && (
        <Vide>Aucun exercice ouvert sur ce dossier : ouvrir un exercice avant de saisir.</Vide>
      )}

      {exercice != null && pieces.isPending && <Chargement quoi="les pièces" />}

      {pieces.data != null && pieces.data.length === 0 && (
        <Vide>Aucune pièce sur cet exercice{journal ? ` dans le journal ${journal}` : ""}.</Vide>
      )}

      {pieces.data != null && pieces.data.length > 0 && (
        <div className="tableau-conteneur">
          <table className="journal">
            <thead>
              <tr>
                <th>Date</th>
                <th>Journal</th>
                <th className="nombre">N°</th>
                <th>Libellé</th>
                <th>Référence</th>
                <th className="nombre">Débit</th>
                <th className="nombre">Crédit</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pieces.data.map((piece) => (
                <PieceEnLigne
                  key={piece.id}
                  piece={piece}
                  depliee={deplies.has(piece.id)}
                  surBascule={() => basculer(piece.id)}
                  actions={
                    <div className="jetons">
                      {piece.estModifiable && peut("SAISIR_ECRITURE") && (
                        <>
                          <button
                            type="button"
                            className="tertiaire"
                            onClick={() => setFormulaire({ ouvert: true, piece })}
                          >
                            Modifier
                          </button>
                          <button
                            type="button"
                            className="tertiaire"
                            disabled={suppression.isPending}
                            onClick={() => demanderLaSuppression(piece)}
                          >
                            Supprimer
                          </button>
                        </>
                      )}
                      {piece.estModifiable && peut("VALIDER_ECRITURE") && (
                        <button
                          type="button"
                          className="tertiaire"
                          disabled={validation.isPending}
                          onClick={() => {
                            setErreur(null);
                            validation.mutate(piece.id);
                          }}
                        >
                          Valider
                        </button>
                      )}
                      {!piece.estModifiable && peut("SAISIR_ECRITURE") && (
                        <button
                          type="button"
                          className="tertiaire"
                          disabled={contrepassation.isPending}
                          onClick={() => demanderLaContrepassation(piece)}
                        >
                          Contrepasser
                        </button>
                      )}
                    </div>
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function PieceEnLigne({
  piece,
  depliee,
  surBascule,
  actions,
}: {
  piece: PieceVue;
  depliee: boolean;
  surBascule: () => void;
  actions: React.ReactNode;
}) {
  return (
    <>
      <tr id={`piece-${piece.id}`} className={piece.statut === "BROUILLARD" ? "brouillard" : undefined}>
        <td>{formaterDate(piece.date)}</td>
        <td>{piece.journal}</td>
        <td className="nombre">{piece.numero ?? "—"}</td>
        <td>
          <a
            href={`#piece-${piece.id}`}
            onClick={(e) => {
              e.preventDefault();
              surBascule();
            }}
          >
            {piece.libelle}
          </a>
          {piece.pieceContrepassee != null && (
            <span className="discret-texte"> · contrepassation</span>
          )}
        </td>
        <td className="discret-texte">{piece.reference ?? "—"}</td>
        <td className="nombre">{formaterMontant(piece.totalDebit)}</td>
        <td className="nombre">{formaterMontant(piece.totalCredit)}</td>
        <td>
          <Etiquette variante={piece.statut === "VALIDEE" ? "succes" : "avertissement"}>
            {piece.statutLibelle}
          </Etiquette>
          {!piece.estIntegre && <Etiquette variante="bloquant">Altérée</Etiquette>}
        </td>
        <td>{actions}</td>
      </tr>
      {depliee && (
        <tr className="detail-piece">
          <td colSpan={9}>
            <LignesDePiece piece={piece} />
            <JustificatifsDePiece piece={piece.id} estModifiable={piece.estModifiable} />
          </td>
        </tr>
      )}
    </>
  );
}
